import type { CSSProperties } from 'react';
import type { FarmCurrentWeather } from '../utils/farmCurrentWeather';
import { weatherIntensity } from '../utils/farmCurrentWeather';

type Props = {
  weather?: FarmCurrentWeather | null;
  className?: string;
};

const DROP_COUNT: Record<'light' | 'medium' | 'heavy', number> = {
  light: 10,
  medium: 16,
  heavy: 24,
};

/**
 * Decorative weather overlay for Saved Field cards.
 * Shows CSS rain / snow / thunder when OpenWeather reports precipitation now.
 */
export default function WeatherAtmosphere({ weather, className = '' }: Props) {
  if (!weather?.is_precipitating) return null;

  const intensity = weatherIntensity(weather);
  const isSnow = weather.condition === 'snow';
  const isThunder = weather.condition === 'thunderstorm';
  const count = DROP_COUNT[intensity];

  return (
    <div
      className={`weather-atmosphere pointer-events-none absolute inset-0 z-[1] overflow-hidden rounded-lg ${className}`}
      aria-hidden
      data-condition={weather.condition}
      data-intensity={intensity}
    >
      <div className={`weather-atmosphere__tint weather-atmosphere__tint--${isSnow ? 'snow' : 'rain'}`} />
      {isThunder && <div className="weather-atmosphere__flash" />}
      <div className={`weather-atmosphere__layer weather-atmosphere__layer--${isSnow ? 'snow' : 'rain'}`}>
        {Array.from({ length: count }, (_, i) => (
          <span
            key={i}
            className={isSnow ? 'weather-flake' : 'weather-drop'}
            style={
              {
                ['--wx-left' as string]: `${(i * 37 + 11) % 100}%`,
                ['--wx-delay' as string]: `${((i * 0.17) % 1.4).toFixed(2)}s`,
                ['--wx-dur' as string]: `${(isSnow ? 2.8 : 0.7) + (i % 5) * 0.12}s`,
                ['--wx-scale' as string]: `${0.7 + (i % 4) * 0.15}`,
              } as CSSProperties
            }
          />
        ))}
      </div>
    </div>
  );
}
